import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';

import { AvatarImage } from '@components/Avatar';
import type { UserAvatarProps } from '@components/Avatar';
import {
  getAverageMapCoordinate,
  getCoordinateViewportPoint,
  getShortestLongitudeDelta,
  regionsEqual,
} from '@components/Explorer/MapGeometry';
import type { ExplorerItem } from '@components/Explorer/explore';
import Icon from '@components/Icon';
import { Marker } from '@components/Map';
import { Caption } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { isDatePast } from '@helpers/Dates';
import { MAP_INITIAL_REGION } from '@helpers/Map';
import type { MapCoordinate, MapRegion, MapViewport } from '@helpers/Map';
import { parseUserAcronym } from '@helpers/Parsers';
import type { Plan, User } from '@types';

const MAP_CLUSTER_LONGITUDE_DELTA = 2.0;
const MAP_MARKER_GROUP_FOCUS_PADDING = 1.6;
const MARKER_AVATAR_SIZE = 34;
const GROUP_COUNT_SIZE = 20;
const MARKER_DETAIL_ICON_SIZE = 14;
const MARKER_ENTITY_BADGE_SIZE = 18;
const MARKER_ENTITY_ICON_SIZE = 12;
const MARKER_AVATAR_SURFACE_MARGIN = 2;
const MARKER_AVATAR_SURFACE_SIZE = MARKER_AVATAR_SIZE + MARKER_AVATAR_SURFACE_MARGIN * 2;
const MARKER_BADGE_BOTTOM_OVERFLOW = 3;
const MARKER_BADGE_RIGHT_OVERFLOW = 5;
const MARKER_BADGE_TOP_OVERFLOW = 5;
const MARKER_COLLISION_PADDING = 10;
const MARKER_DETAIL_BADGE_MARGIN_LEFT = 4;
const MARKER_DETAIL_BADGE_MAX_WIDTH = 112;
const MARKER_AVATAR_CONTAINER_WIDTH = MARKER_AVATAR_SURFACE_SIZE + MARKER_BADGE_RIGHT_OVERFLOW;
const MARKER_AVATAR_CONTAINER_HEIGHT =
  MARKER_BADGE_TOP_OVERFLOW + MARKER_AVATAR_SURFACE_SIZE + MARKER_BADGE_BOTTOM_OVERFLOW;
const MARKER_AVATAR_CENTER_X = MARKER_AVATAR_SURFACE_SIZE / 2;
const MARKER_AVATAR_CENTER_Y = MARKER_BADGE_TOP_OVERFLOW + MARKER_AVATAR_SURFACE_SIZE / 2;
const MARKER_WITH_DETAILS_WIDTH =
  MARKER_AVATAR_CONTAINER_WIDTH + MARKER_DETAIL_BADGE_MARGIN_LEFT + MARKER_DETAIL_BADGE_MAX_WIDTH;
const MARKER_AVATAR_ANCHOR = {
  x: MARKER_AVATAR_CENTER_X / MARKER_AVATAR_CONTAINER_WIDTH,
  y: MARKER_AVATAR_CENTER_Y / MARKER_AVATAR_CONTAINER_HEIGHT,
};
const MARKER_WITH_DETAILS_ANCHOR = {
  x: MARKER_AVATAR_CENTER_X / MARKER_WITH_DETAILS_WIDTH,
  y: MARKER_AVATAR_CENTER_Y / MARKER_AVATAR_CONTAINER_HEIGHT,
};
const MARKER_COLLISION_DIAMETER =
  Math.max(
    MARKER_AVATAR_SURFACE_SIZE + MARKER_BADGE_RIGHT_OVERFLOW,
    MARKER_AVATAR_SURFACE_SIZE + MARKER_BADGE_TOP_OVERFLOW + MARKER_BADGE_BOTTOM_OVERFLOW
  ) + MARKER_COLLISION_PADDING;
const MARKER_TEST_ID_PREFIX = 'components.Explorer.MapMarker';
export const ANDROID_MARKER_VIEW_TRACKING_TIMEOUT_MS = 250;

export type MapMarkerEntityType = 'group' | 'user';

export type MapMarkerProfile = Pick<User, 'displayName' | 'photoUrl'> & {
  entityType?: MapMarkerEntityType;
};

export type MapMarkerDetail = {
  captionIcon: React.ComponentProps<typeof Icon>['name'];
  captionText: string;
};

export type MapMarkerGroup = {
  key: string;
  coordinate: ExplorerItem['coordinate'];
  items: ExplorerItem[];
};

type MapMarkersProps = {
  items: ExplorerItem[];
  mapRegion: MapRegion;
  mapViewport: MapViewport;
  setMapRegion: (region: MapRegion) => void;
  onPlanPress: (plan: Plan) => void;
  onUserPress?: (user: User) => void;
  showDetails?: boolean;
};

function MapMarkersComponent({
  items,
  mapRegion,
  mapViewport,
  setMapRegion,
  onPlanPress,
  onUserPress,
  showDetails = false,
}: MapMarkersProps) {
  const markerGroups = React.useMemo(
    () => getMapMarkerGroups(items, mapRegion, mapViewport),
    [items, mapRegion, mapViewport]
  );

  return (
    <>
      {markerGroups.flatMap((markerGroup) => {
        const singleItem = markerGroup.items.length === 1 ? markerGroup.items[0] : undefined;
        const firstItem = markerGroup.items[0];
        if (!firstItem) return [];

        return (
          <ExplorerMarker
            key={markerGroup.key}
            count={markerGroup.items.length > 1 ? markerGroup.items.length : undefined}
            coordinate={markerGroup.coordinate}
            identifier={markerGroup.key}
            item={firstItem}
            showDetails={showDetails}
            title={getMarkerGroupTitle(markerGroup)}
            description={singleItem ? undefined : 'Tap to zoom'}
            onPress={() => {
              if (singleItem?.type === 'plan') {
                onPlanPress(singleItem.plan);
                return;
              }
              if (singleItem?.type === 'user') {
                onUserPress?.(singleItem.user);
                return;
              }
              const focusRegion = getMarkerGroupFocusRegion(markerGroup, mapRegion);
              if (regionsEqual(focusRegion, mapRegion)) return;

              setMapRegion(focusRegion);
            }}
          />
        );
      })}
    </>
  );
}

export const MapMarkers = React.memo(MapMarkersComponent);

type ExplorerMarkerProps = Omit<
  React.ComponentProps<typeof Marker>,
  'children' | 'tracksViewChanges'
> & {
  count?: number;
  item: ExplorerItem;
  showDetails: boolean;
};

function ExplorerMarker({ anchor, count, item, showDetails, ...markerProps }: ExplorerMarkerProps) {
  const { markerImageProps, tracksViewChanges } = useAndroidMarkerViewTracking(
    item.id,
    item.profile.photoUrl
  );

  return (
    <Marker
      {...markerProps}
      anchor={anchor ?? getDefaultMarkerAnchor(showDetails)}
      testID={`${MARKER_TEST_ID_PREFIX}.${item.id}`}
      tracksViewChanges={tracksViewChanges}>
      <MapItemMarker
        count={count}
        item={item}
        markerImageProps={markerImageProps}
        showDetails={showDetails}
      />
    </Marker>
  );
}

function getDefaultMarkerAnchor(showDetails: boolean) {
  if (Platform.OS !== 'android') return undefined;

  return showDetails ? MARKER_WITH_DETAILS_ANCHOR : MARKER_AVATAR_ANCHOR;
}

function useAndroidMarkerViewTracking(
  itemId: string,
  photoUrl: string
): {
  markerImageProps?: UserAvatarProps['imageProps'];
  tracksViewChanges: boolean;
} {
  const shouldTrackViewChanges = Platform.OS === 'android';
  const trackingKey = `${itemId}:${photoUrl}`;
  const [trackingState, setTrackingState] = React.useState({
    key: trackingKey,
    tracksViewChanges: shouldTrackViewChanges,
  });
  const tracksViewChanges =
    trackingState.key === trackingKey ? trackingState.tracksViewChanges : shouldTrackViewChanges;
  const stopTrackingViewChanges = React.useCallback(() => {
    if (shouldTrackViewChanges) {
      setTrackingState({ key: trackingKey, tracksViewChanges: false });
    }
  }, [shouldTrackViewChanges, trackingKey]);
  const markerImageProps = React.useMemo(
    () =>
      shouldTrackViewChanges && photoUrl.length > 0
        ? {
            disablePlaceholder: true,
            onDisplay: stopTrackingViewChanges,
            onError: stopTrackingViewChanges,
            testID: `${MARKER_TEST_ID_PREFIX}.AvatarImage.${itemId}`,
          }
        : undefined,
    [itemId, photoUrl.length, shouldTrackViewChanges, stopTrackingViewChanges]
  );

  React.useEffect(() => {
    if (!shouldTrackViewChanges || photoUrl.length > 0) return undefined;

    const timeout = setTimeout(stopTrackingViewChanges, ANDROID_MARKER_VIEW_TRACKING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [photoUrl, shouldTrackViewChanges, stopTrackingViewChanges]);

  return { markerImageProps, tracksViewChanges };
}

function MapItemMarker({
  count,
  item,
  markerImageProps,
  showDetails,
}: {
  count?: number;
  item: ExplorerItem;
  markerImageProps?: UserAvatarProps['imageProps'];
  showDetails: boolean;
}) {
  const detail = getMapMarkerDetail(item);
  const entityIcon = getMapMarkerEntityIcon(getMapMarkerEntity(item));
  const appTheme = useAppTheme();
  const surfaceStyle = { backgroundColor: appTheme.colors.surface };
  const fallbackAvatarStyle = {
    backgroundColor: appTheme.colors.primary,
    borderColor: appTheme.colors.surface,
  };
  const accentBadgeStyle = {
    backgroundColor: appTheme.colors.accent,
    borderColor: appTheme.colors.surface,
  };
  const accentTextStyle = { color: appTheme.colors.accent };
  const surfaceTextStyle = { color: appTheme.colors.surface };

  return (
    <View style={[styles.marker, showDetails ? styles.markerWithDetails : undefined]}>
      <View
        style={styles.avatarContainer}
        testID={`${MARKER_TEST_ID_PREFIX}.AvatarContainer.${item.id}`}>
        {item.profile.photoUrl ? (
          <View
            style={[styles.avatarSurface, surfaceStyle]}
            testID="components.Explorer.MapMarker.AvatarSurface">
            <AvatarImage
              source={{ uri: item.profile.photoUrl }}
              size={MARKER_AVATAR_SIZE}
              {...markerImageProps}
            />
          </View>
        ) : (
          <View
            style={[styles.fallbackAvatarSurface, fallbackAvatarStyle]}
            testID="components.Explorer.MapMarker.AvatarSurface">
            <Text style={[styles.fallbackAvatarText, surfaceTextStyle]} numberOfLines={1}>
              {parseUserAcronym(item.profile.displayName)}
            </Text>
          </View>
        )}
        {showDetails ? (
          <View
            style={[styles.entityBadge, accentBadgeStyle]}
            testID={`${MARKER_TEST_ID_PREFIX}.EntityBadge.${item.id}`}>
            <Icon
              name={entityIcon}
              color={appTheme.colors.surface}
              size={MARKER_ENTITY_ICON_SIZE}
            />
          </View>
        ) : null}
        {count ? (
          <View
            style={[styles.countBadge, accentBadgeStyle]}
            testID={`${MARKER_TEST_ID_PREFIX}.CountBadge.${item.id}`}>
            <Caption style={[styles.countText, surfaceTextStyle]}>{count}</Caption>
          </View>
        ) : null}
      </View>
      {showDetails ? (
        <View
          style={[styles.detailBadge, surfaceStyle]}
          testID="components.Explorer.MapMarker.DetailBadge">
          <Icon
            name={detail.captionIcon}
            color={appTheme.colors.accent}
            size={MARKER_DETAIL_ICON_SIZE}
            style={styles.detailIcon}
          />
          <Caption numberOfLines={1} style={[styles.detailText, accentTextStyle]}>
            {detail.captionText}
          </Caption>
        </View>
      ) : null}
    </View>
  );
}

export function getMapMarkerEntity(item: ExplorerItem): MapMarkerEntityType {
  if (item.type === 'user') return 'user';

  return item.profile.entityType || 'user';
}

export function getMapMarkerDetail(item: ExplorerItem): MapMarkerDetail {
  if (item.type === 'user') {
    return {
      captionIcon: 'home-outline',
      captionText: 'Resident',
    };
  }

  const past = isDatePast(item.plan.endDate);
  return {
    captionIcon: past ? 'pin-outline' : 'sign-direction',
    captionText: past ? 'Visited' : 'Upcoming',
  };
}

function getMapMarkerEntityIcon(entityType: MapMarkerEntityType) {
  return entityType === 'group' ? 'account-multiple' : 'account';
}

export function getMapMarkerGroups(
  items: ExplorerItem[],
  mapRegion: MapRegion,
  mapViewport: MapViewport
): MapMarkerGroup[] {
  const groups: MapMarkerGroup[] = [];

  items.forEach((item) => {
    const markerGroup = groups.find((group) =>
      coordinatesCollideInViewport(item.coordinate, group.coordinate, mapRegion, mapViewport)
    );

    if (markerGroup) {
      markerGroup.items.push(item);
      markerGroup.coordinate = getAverageCoordinate(markerGroup.items);
      markerGroup.key = getMarkerGroupKey(markerGroup.items);
      return;
    }

    groups.push({
      key: getMarkerGroupKey([item]),
      coordinate: item.coordinate,
      items: [item],
    });
  });

  return groups;
}

function getMarkerGroupTitle(markerGroup: MapMarkerGroup) {
  const firstItem = markerGroup.items[0];
  return markerGroup.items.length === 1 && firstItem
    ? firstItem.profile.displayName
    : `${markerGroup.items.length} people and plans`;
}

function getMarkerGroupFocusRegion(markerGroup: MapMarkerGroup, mapRegion: MapRegion): MapRegion {
  const maxLatitudeDistance = Math.max(
    ...markerGroup.items.map((item) =>
      Math.abs(item.coordinate.latitude - markerGroup.coordinate.latitude)
    )
  );
  const maxLongitudeDistance = Math.max(
    ...markerGroup.items.map((item) =>
      Math.abs(
        getShortestLongitudeDelta(markerGroup.coordinate.longitude, item.coordinate.longitude)
      )
    )
  );
  const minDelta = MAP_CLUSTER_LONGITUDE_DELTA / 2;

  return {
    latitude: markerGroup.coordinate.latitude,
    longitude: markerGroup.coordinate.longitude,
    latitudeDelta: Math.min(
      Math.max(maxLatitudeDistance * 2 * MAP_MARKER_GROUP_FOCUS_PADDING, minDelta),
      MAP_INITIAL_REGION.latitudeDelta,
      getNormalizedRegionDelta(mapRegion.latitudeDelta)
    ),
    longitudeDelta: Math.min(
      Math.max(maxLongitudeDistance * 2 * MAP_MARKER_GROUP_FOCUS_PADDING, minDelta),
      MAP_INITIAL_REGION.longitudeDelta,
      getNormalizedRegionDelta(mapRegion.longitudeDelta)
    ),
  };
}

function coordinatesCollideInViewport(
  coordinateA: MapCoordinate,
  coordinateB: MapCoordinate,
  mapRegion: MapRegion,
  mapViewport: MapViewport
) {
  const pointA = getCoordinateViewportPoint(coordinateA, mapRegion, mapViewport);
  const pointB = getCoordinateViewportPoint(coordinateB, mapRegion, mapViewport);

  return getPointDistance(pointA, pointB) < MARKER_COLLISION_DIAMETER;
}

function getPointDistance(pointA: { x: number; y: number }, pointB: { x: number; y: number }) {
  return Math.sqrt((pointA.x - pointB.x) ** 2 + (pointA.y - pointB.y) ** 2);
}

function getNormalizedRegionDelta(delta: number) {
  return Math.max(Math.abs(delta), Number.EPSILON);
}

function getMarkerGroupKey(items: ExplorerItem[]) {
  const firstItem = items[0];
  if (items.length === 1 && firstItem) return firstItem.id;

  return `cluster:${items.map((item) => item.id).join('|')}`;
}

function getAverageCoordinate(items: ExplorerItem[]) {
  const [firstItem, ...remainingItems] = items;

  if (!firstItem) {
    throw new Error('Cannot average an empty marker group');
  }

  return getAverageMapCoordinate([
    firstItem.coordinate,
    ...remainingItems.map((item) => item.coordinate),
  ]);
}

const styles = StyleSheet.create({
  avatarContainer: {
    paddingBottom: MARKER_BADGE_BOTTOM_OVERFLOW,
    paddingRight: MARKER_BADGE_RIGHT_OVERFLOW,
    paddingTop: MARKER_BADGE_TOP_OVERFLOW,
    position: 'relative',
  },
  avatarSurface: {
    alignItems: 'center',
    borderRadius: MARKER_AVATAR_SURFACE_SIZE / 2,
    elevation: 3,
    height: MARKER_AVATAR_SURFACE_SIZE,
    justifyContent: 'center',
    overflow: 'visible',
    padding: MARKER_AVATAR_SURFACE_MARGIN,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    width: MARKER_AVATAR_SURFACE_SIZE,
  },
  countBadge: {
    alignItems: 'center',
    borderRadius: GROUP_COUNT_SIZE / 2,
    borderWidth: 2,
    elevation: 4,
    height: GROUP_COUNT_SIZE,
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    top: 0,
    width: GROUP_COUNT_SIZE,
    zIndex: 2,
  },
  countText: {
    fontSize: 10,
    fontWeight: '700',
    lineHeight: 12,
  },
  detailBadge: {
    alignItems: 'center',
    borderRadius: 8,
    elevation: 3,
    flexDirection: 'row',
    marginLeft: MARKER_DETAIL_BADGE_MARGIN_LEFT,
    maxWidth: MARKER_DETAIL_BADGE_MAX_WIDTH,
    paddingHorizontal: 6,
    paddingVertical: 3,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
  },
  detailIcon: {
    flexShrink: 0,
    marginRight: 3,
  },
  detailText: {
    flexShrink: 1,
    fontSize: 11,
    fontWeight: '600',
    lineHeight: 13,
    marginVertical: 0,
  },
  entityBadge: {
    alignItems: 'center',
    borderRadius: MARKER_ENTITY_BADGE_SIZE / 2,
    borderWidth: 2,
    bottom: 0,
    elevation: 4,
    height: MARKER_ENTITY_BADGE_SIZE,
    justifyContent: 'center',
    position: 'absolute',
    right: MARKER_BADGE_RIGHT_OVERFLOW - 4,
    width: MARKER_ENTITY_BADGE_SIZE,
    zIndex: 2,
  },
  fallbackAvatarSurface: {
    alignItems: 'center',
    borderRadius: MARKER_AVATAR_SURFACE_SIZE / 2,
    borderWidth: MARKER_AVATAR_SURFACE_MARGIN,
    elevation: 3,
    height: MARKER_AVATAR_SURFACE_SIZE,
    justifyContent: 'center',
    overflow: 'hidden',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    width: MARKER_AVATAR_SURFACE_SIZE,
  },
  fallbackAvatarText: {
    fontSize: 17,
    fontWeight: '500',
    lineHeight: 20,
    textAlign: 'center',
  },
  marker: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    justifyContent: 'center',
    overflow: 'visible',
  },
  markerWithDetails: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: MARKER_WITH_DETAILS_WIDTH,
  },
});
